<template>
  <validation-observer
    v-slot="{ handleSubmit }"
    ref="observer"
    tag="div"
    class="modern-card modal-card social-media-image-template-form"
  >
      <b-loading :is-full-page="false" :active="isLoading" :can-cancel="false"></b-loading>

      <header class="modern-card__header">
        {{ isUpdate ? 'Edit image' : 'Add image' }}

        <b-button
          class="modern-card__close-button"
          aria-label="close"
          icon-right="close"
          @click="$emit('close')"
        />
      </header>

      <section class="modern-card__body">
        <form class="modern-modal-form">
          <ModernField
            v-model="form.name"
            label="Name*"
            :rules="{
              required: true,
              max: 50,
              latin_characters_digits_spaces: true,
            }"
          >
            <ModernTextInput
              v-model="form.name"
              maxlength="50"
            />
          </ModernField>

          <ModernField
            :value="imageFileSrc || form.imageUrl"
            name="imageUrl"
            label="Image*"
            rules="required"
          >
            <div class="drag-drop-container">
              <ModernDragDropInput
                accept="image/jpeg, image/png, image/gif"
                :files.sync="selectedFiles"
                @select="onFileSelect"
              >
                <template #filesContent>
                  <div class="drag-drop-image-wrapper">
                    <img
                      class="drag-drop-image"
                      :src="imageFileSrc || form.imageUrl"
                      :alt="imageFileName || form.imageFilename"
                    >
                  </div>
                </template>
              </ModernDragDropInput>
            </div>
          </ModernField>

          <ModernField
            v-model="isBodyInputVisible"
            label=""
          >
            <div>
              <div>
                <b-switch
                  class="enh-switch"
                  v-model="isBodyInputVisible"
                  position="is-right"
                  size="is-small"
                  type="is-green"
                />
                <span>HTML-Code</span>
              </div>

              <div v-if="isBodyInputVisible">
                <ModernField
                  v-model="form.body"
                  label=""
                  rules="required"
                >
                  <b-input
                    v-model="form.body"
                    type="textarea"
                    rows="8"
                  />
                </ModernField>

                <b-button
                  class="modern-button is-outlined"
                  style="max-width: 160px; width: 160px; margin-top: .75em; flex-grow: 1"
                  @click="onPreviewClick"
                >
                  PREVIEW
                </b-button>
              </div>
            </div>
          </ModernField>

          <ModernField
            v-model="form.socialMediaId"
            label="Social media*"
            rules="required"
          >
            <b-select size="is-medium" v-model="form.socialMediaId" expanded>
              <option
                v-for="item in socialMedias"
                v-bind:key="item.id"
                :value="item.id"
              >
                {{ item.value }}
              </option>
            </b-select>
          </ModernField>

          <ModernField
            v-model="form.contentTypeId"
            label="Content type*"
            rules="required"
          >
            <b-select
              v-model="form.contentTypeId"
              :disabled="!form.socialMediaId"
              size="is-medium"
              expanded
            >
              <option
                v-for="item in socialMediaContentTypeOptions"
                v-bind:key="item.id"
                :value="item.id"
              >
                {{ item.value }}
              </option>
            </b-select>
          </ModernField>

          <ModernField
            v-model="form.userTypeId"
            label="User*"
            rules="required"
          >
            <b-select
              v-model.number="form.userTypeId"
              size="is-medium"
              expanded
            >
              <option
                v-for="(option, idx) in userTypes"
                :key="idx"
                :value="option.id"
              >
                <template v-if="option.isCustomerType">
                  Customer:
                </template>
                {{ option.value }}
              </option>
            </b-select>
          </ModernField>

          <ModernField
            v-model="form.themeId"
            label="Theme*"
            rules="required"
          >
            <b-select
              v-model="form.themeId"
              :disabled="!form.userTypeId"
              size="is-medium"
              expanded
            >
              <option
                v-for="item in themeOptions"
                v-bind:key="item.id"
                :value="item.id"
              >
                {{ item.name }}
              </option>
            </b-select>
          </ModernField>

          <ModernField
            v-model="form.order"
            label="Order*"
            rules="required"
          >
            <ModernNumberInput
              v-model="form.order"
              :min="1"
              :max="100"
            />
          </ModernField>
        </form>
      </section>

      <footer class="modern-card__footer">
          <button
            type="button"
            class="button is-success outlined"
            @click="$emit('close')"
          >
            Cancel
          </button>

          <button
            type="button"
            class="button is-success"
            @click="handleSubmit(submit)"
          >
            {{ isUpdate ? 'Update' : 'Create' }}
          </button>
        </footer>
  </validation-observer>
</template>

<script>
import ModernField from '@/components/Form/Field/ModernField.vue';
import ModernTextInput from '@/components/Form/Input/ModernTextInput.vue';
import SocialMediaImageTemplate from '@/entity/SocialMediaImageTemplate';
import {
  create, getPreview,
  read,
  update,
  uploadImage, validateImage,
} from '@/api/social_media_image_templates';
import { get } from 'lodash';
import { mapGetters, mapState } from 'vuex';
import ModernDragDropInput from '@/components/Form/Input/ModernDragDropInput.vue';
import getEntityChangedFields from '@/utils/getEntityChangedFields';
import { SOCIAL_MEDIA_CONTENT_TYPES_MAP } from '@/const/constants';
import preloadImage from '@/utils/preloadImage';
import ModernNumberInput from '@/components/Form/Input/ModernNumberInput.vue';

export default {
  name: 'SocialMediaImageTemplateForm',
  components: {
    ModernNumberInput,
    ModernTextInput,
    ModernField,
    ModernDragDropInput,
  },

  props: {
    id: {
      type: Number,
      default: null,
    },
    themes: {
      type: Array,
      default: () => ([]),
    },
  },

  data() {
    return {
      isLoading: false,
      entity: new SocialMediaImageTemplate(),
      form: new SocialMediaImageTemplate(),
      isBodyInputVisible: false,
      selectedFiles: [],
      imageFile: null,
    };
  },

  async mounted() {
    this.isLoading = true;

    try {
      if (this.isUpdate) {
        const entityResponse = await read(this.id);
        this.entity.loadData(entityResponse.data);
        this.form.loadData(entityResponse.data);
        this.isBodyInputVisible = this.entity.body && this.entity.body.length > 0;
        this.selectedFiles = [this.form.imageUrl];
      }
    } catch (error) {
      this.$buefy.snackbar.open({
        message: get(error, 'response.data.message', 'An unexpected error occurred, please contact support@enhancify.com'),
        actionText: ' ',
        position: 'is-top',
        indefinite: true,
        type: 'is-danger',
      });
      this.$emit('close');
    }

    if (this.isUpdate) {
      try {
        await preloadImage(this.form.imageUrl);
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to load image',
          type: 'is-danger',
          duration: 5000,
        });
      }
    }

    this.isLoading = false;
  },

  watch: {
    'form.userTypeId': function formUserTypeIdWatcher(value, oldValue) {
      if (oldValue !== undefined) {
        this.form.themeId = null;
      }
    },
    'form.socialMediaId': function formSocialMediaIdWatcher(value, oldValue) {
      if (oldValue !== undefined) {
        this.form.contentTypeId = null;
      }
    },
    isBodyInputVisible(value) {
      if (value === false) {
        this.form.body = null;
      }
    },
  },

  computed: {
    ...mapGetters('constant', ['userTypes']),
    ...mapState('constant', ['socialMedias', 'socialMediaContentTypes']),
    socialMediaContentTypeOptions() {
      const allowedIds = this.form.socialMediaId
        ? SOCIAL_MEDIA_CONTENT_TYPES_MAP[this.form.socialMediaId]
        : [];

      return this.socialMediaContentTypes.filter((item) => allowedIds.includes(item.id));
    },
    themeOptions() {
      return this.themes.filter((item) => item.userType.id === this.form.userTypeId);
    },
    isUpdate() {
      return this.id !== null;
    },
    imageFileSrc() {
      return this.imageFile ? URL.createObjectURL(this.imageFile) : null;
    },
    imageFileName() {
      return this.imageFile ? this.imageFile.name : null;
    },
  },

  methods: {
    async submit() {
      this.isLoading = true;

      try {
        if (this.imageFile) {
          const formData = new FormData();
          const uploadFile = new File([this.imageFile], 'uploadImage', { type: 'image/png' });
          formData.set('image', uploadFile);

          const response = await uploadImage(this.isUpdate ? this.id : 'null', formData);
          this.form.imageFilename = response.data.imageFilename;
          this.form.imageUrl = response.data.imageUrl;
        }

        const payload = getEntityChangedFields(this.entity, this.form);

        const { data } = this.isUpdate
          ? await update(this.id, payload)
          : await create(payload);

        this.$buefy.toast.open({
          message: 'Update successful!',
          type: 'is-success',
          duration: 5000,
        });

        this.$emit('submit', { data });
        this.$emit('close');
      } catch (error) {
        this.$buefy.snackbar.open({
          message: get(error, 'response.data.message', 'An unexpected error occurred, please contact support@enhancify.com'),
          actionText: ' ',
          position: 'is-top',
          indefinite: true,
          type: 'is-danger',
        });
      }

      this.isLoading = false;
    },
    isValidSelectedFile(file) {
      let errorMessage = null;

      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
      const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif'];
      const maxSize = 5 * 1024 * 1024;

      const extension = file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(file.type) || !allowedExtensions.includes(extension)) {
        errorMessage = 'Please upload a file in the PNG, GIF, or JPG format ';
      } else if (file.size > maxSize) {
        errorMessage = 'The uploading file cannot be more than 5 MB';
      }

      if (errorMessage) {
        this.$refs.observer.setErrors({ imageUrl: errorMessage });
      }

      return errorMessage === null;
    },
    async onFileSelect(files) {
      this.imageFile = null;

      if (this.isValidSelectedFile(files[0])) {
        const formData = new FormData();
        const sourceFile = files[0];
        const uploadFile = new File([sourceFile], 'uploadImage', { type: 'image/png' });
        formData.set('image', uploadFile);

        this.isLoading = true;

        try {
          await validateImage(formData);

          this.form.imageFilename = null;
          this.form.imageUrl = null;

          this.imageFile = sourceFile;
        } catch (error) {
          this.imageFile = null;

          this.$buefy.snackbar.open({
            message: get(error, 'response.data.message', 'An unexpected error occurred, please contact support@enhancify.com'),
            actionText: ' ',
            position: 'is-top',
            indefinite: true,
            type: 'is-danger',
          });
        }

        this.isLoading = false;
      } else {
        this.selectedFiles = this.form.imageUrl ? [this.form.imageUrl] : [];
      }
    },

    async onPreviewClick() {
      this.isLoading = true;

      try {
        const response = await getPreview(this.id ?? 'null', {
          imageFilename: this.form.imageFilename,
          body: this.form.body,
        });

        const blob = response.data;
        const url = window.URL.createObjectURL(blob);

        window.open(url, '_blank');
      } catch (error) {
        this.$buefy.snackbar.open({
          message: get(error, 'response.data.message', 'An unexpected error occurred, please contact support@enhancify.com'),
          actionText: ' ',
          position: 'is-top',
          indefinite: true,
          type: 'is-danger',
        });
      }

      this.isLoading = false;
    },
  },

};
</script>

<style lang="stylus">
.social-media-image-template-form
  .field .control:not(:has(textarea))
    height 48px
    input, .select, .select select
      height 100%
      font-size 1.1875rem
  .drag-drop-container
    height 215px
    & .drag-drop-image-wrapper
      pointer-events none
      height 100%
      width 100%
      & .drag-drop-image
        height 100%
        width 100%
        object-fit contain
</style>

<style scoped lang="stylus">
</style>
